import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;
const BASE_H = 1.25 * IN;
const BASE_RECESS = 0.5 * IN;
const DOOR_TRIM = 0.5 * IN;

// ── Compact ─────────────────────────────────────────────────────────

const KNOB_R = 0.35 * IN;

export function DormMiniFridgeCompactSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <FridgeSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  const bodyBottom = height - BASE_H;
  const doorX = DOOR_TRIM;
  const doorY = DOOR_TRIM;
  const doorW = width - DOOR_TRIM * 2;
  const doorH = bodyBottom - DOOR_TRIM * 2;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <Line
        x1={0}
        y1={bodyBottom}
        x2={width}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <rect
        x={doorX}
        y={doorY}
        width={doorW}
        height={doorH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={width - DOOR_TRIM - KNOB_R - 0.5 * IN}
        cy={DOOR_TRIM + KNOB_R + 0.3 * IN}
        r={KNOB_R}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}

DormMiniFridgeCompactSchematic.supportsSideViews = true;

// ── Mid-size ────────────────────────────────────────────────────────

const FREEZER_RATIO = 0.3;
const DOOR_GAP = 0.5 * IN;
const HANDLE_MARGIN = 1.5 * IN;

export function DormMiniFridgeMidsizeSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <FridgeSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  const bodyBottom = height - BASE_H;
  const totalDoorH = bodyBottom - DOOR_TRIM * 2;
  const freezerH = totalDoorH * FREEZER_RATIO - DOOR_GAP / 2;
  const fridgeH = totalDoorH * (1 - FREEZER_RATIO) - DOOR_GAP / 2;

  const doorX = DOOR_TRIM;
  const doorW = width - DOOR_TRIM * 2;
  const handleW = doorW * 0.65;
  const handleX = (width - handleW) / 2;

  const freezerY = DOOR_TRIM;
  const fridgeY = DOOR_TRIM + freezerH + DOOR_GAP;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <Line
        x1={0}
        y1={bodyBottom}
        x2={width}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <rect
        x={doorX}
        y={freezerY}
        width={doorW}
        height={freezerH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={doorX}
        y={fridgeY}
        width={doorW}
        height={fridgeH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <Line
        x1={handleX}
        y1={freezerY + freezerH - HANDLE_MARGIN}
        x2={handleX + handleW}
        y2={freezerY + freezerH - HANDLE_MARGIN}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={handleX}
        y1={fridgeY + HANDLE_MARGIN}
        x2={handleX + handleW}
        y2={fridgeY + HANDLE_MARGIN}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

DormMiniFridgeMidsizeSchematic.supportsSideViews = true;

function FridgeSide({ depth, height, strokeColor, strokeWidth }) {
  const bodyBottom = height - BASE_H;
  return (
    <g>
      <Line
        x1={0}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={depth - BASE_RECESS}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}
